/**
 * Printable goods receipt slip (GRN phase 1, 2026-10-21, decision g16): one A4 page per receipt for the paper
 * trail — what arrived, from whom, for which job or store, with signature lines for procurement and the receiving
 * department. Pure functions: data in, a self-contained HTML document out (opened and printed like the MR print).
 */
export interface GrnSlipLine {
  no: number; code?: string | null; item: string; goesTo?: string | null; accepted: number; rejected: number;
  unit?: string | null; price?: number | null; outcome?: string | null;
}
export interface GrnSlipData {
  grnId: string; poId: string; path?: string | null; vendor: string; department?: string | null;
  receivedOn: string; receivedBy?: string | null; paidBy?: string | null; deliveryNote?: string | null; invoice?: string | null;
  notes?: string | null; lines: GrnSlipLine[];
  /** Who asked for the goods (MR requester names), printed on the "Handed to" signature line. */
  requester?: string | null;
}
type SlipOpts = { printedAt: string; printedBy: string };

const esc = (v: unknown) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const n2 = (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const q = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 2 });

const STYLE = `
  @page { size: A4; margin: 14mm; }
  body { font: 12px/1.45 -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; color: #111; margin: 0; }
  .slip { page-break-after: always; break-after: page; }
  .slip:last-child { page-break-after: auto; break-after: auto; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; border-bottom: 2px solid #111; padding-bottom: 10px; }
  .brand { font: 600 12px/1 Georgia, serif; letter-spacing: .3em; text-transform: uppercase; }
  h2 { margin: 6px 0 0; font-size: 18px; }
  .hr { text-align: right; }
  .meta { display: flex; flex-wrap: wrap; gap: 2px 18px; margin: 8px 0 12px; font-size: 11.5px; color: #333; }
  .meta b { color: #777; font-weight: 500; margin-right: 4px; }
  .meta .wide { flex-basis: 100%; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-weight: 600; border-bottom: 1px solid #111; padding: 6px 4px; font-size: 11px; text-transform: uppercase; letter-spacing: .3px; }
  td { border-bottom: 1px solid #ddd; padding: 6px 4px; vertical-align: top; }
  tr { page-break-inside: avoid; }
  .r { text-align: right; white-space: nowrap; } .n { width: 22px; color: #555; }
  .code { font: 600 11px ui-monospace, Menlo, monospace; }
  .desc { color: #666; }
  .rej { color: #a8071a; font-size: 11px; margin-top: 2px; }
  .tot td { border-bottom: 0; border-top: 1px solid #111; font-weight: 600; }
  .sign { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 24px; margin-top: 36px; page-break-inside: avoid; }
  .sign div { border-top: 1px solid #111; padding-top: 4px; font-size: 11px; color: #333; }
  .foot { margin-top: 18px; font-size: 10px; color: #777; }
`;

const page = (title: string, body: string) => `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>${STYLE}</style></head><body>${body}
</body></html>`;

// 2026-10-22: layout per the approved mockup — brand + receipt id on the left; PO · path / vendor · bought|received
// date / paid (direct purchases) on the right; department, receiver, documents and notes in one compact line under it.
function slipBody(g: GrnSlipData, opts: SlipOpts): string {
  const direct = g.path === 'Direct purchase'; // PATH_LABEL.direct in components/receiving/shared
  const day = new Date(g.receivedOn).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const value = (l: GrnSlipLine) => l.accepted * Number(l.price || 0);
  const total = g.lines.reduce((s, l) => s + value(l), 0);
  const perUnit = new Map<string, number>();
  g.lines.forEach((l) => perUnit.set(l.unit || '', (perUnit.get(l.unit || '') || 0) + l.accepted));
  const unitsText = Array.from(perUnit.entries()).map(([u, n]) => `${q(n)} ${u}`.trim()).join(' · ');
  const docs = [g.deliveryNote ? `DN ${g.deliveryNote}` : '', g.invoice ? `Invoice ${g.invoice}` : ''].filter(Boolean).join(' · ');
  const meta = [
    g.department ? `<span><b>Department</b>${esc(g.department)}</span>` : '',
    g.receivedBy ? `<span><b>Received by</b>${esc(g.receivedBy)}</span>` : '',
    docs ? `<span><b>Documents</b>${esc(docs)}</span>` : '',
    g.notes ? `<span class="wide"><b>Notes</b>${esc(g.notes)}</span>` : '',
  ].filter(Boolean).join('');
  const handedTo = [g.department, g.requester].filter(Boolean).join(' · ');

  const rows = g.lines.map((l) => `
    <tr>
      <td class="n">${l.no}</td>
      <td>${l.code ? `<span class="code">${esc(l.code)}</span><br>` : ''}<span class="desc">${esc(l.item)}</span>${l.rejected > 0 ? `<div class="rej">${q(l.rejected)} ${esc(l.unit || '')} rejected${l.outcome ? ` · ${esc(l.outcome)}` : ''}</div>` : ''}</td>
      <td>${esc(l.goesTo || '')}</td>
      <td class="r">${q(l.accepted)} ${esc(l.unit || '')}</td>
      <td class="r">${l.price != null ? n2(Number(l.price)) : ''}</td>
      <td class="r">${n2(value(l))}</td>
    </tr>`).join('');

  return `
<section class="slip">
  <div class="head">
    <div><div class="brand">SHAHIRA LASHEEN</div><h2>Goods receipt ${esc(g.grnId)}</h2></div>
    <div class="hr">${esc(g.poId)}${g.path ? ` · ${esc(g.path)}` : ''}<br>${g.vendor ? `${esc(g.vendor)} · ` : ''}${direct ? 'bought' : 'received'} ${esc(day)}${direct && g.paidBy ? `<br>Paid: ${esc(g.paidBy)}` : ''}</div>
  </div>
  <div class="meta">${meta}</div>
  <table>
    <thead><tr><th>#</th><th>Item</th><th>For</th><th class="r">Qty</th><th class="r">Price</th><th class="r">Value</th></tr></thead>
    <tbody>${rows}
      <tr class="tot"><td></td><td colspan="2">Total</td><td class="r">${esc(unitsText)}</td><td></td><td class="r">${n2(total)}</td></tr>
    </tbody>
  </table>
  <div class="sign">
    <div>Received by (procurement)<br>${esc(g.receivedBy || '')}</div>
    <div>Handed to (department)<br>${handedTo ? esc(handedTo) : '&nbsp;'}</div>
    <div>Date<br>&nbsp;</div>
  </div>
  <div class="foot">Printed ${esc(opts.printedAt)} by ${esc(opts.printedBy)}</div>
</section>`;
}

export function buildGrnSlipHtml(g: GrnSlipData, opts: SlipOpts): string {
  return page(g.grnId, slipBody(g, opts));
}

/** Several receipts in one print job: the same slip markup, each on its own A4 page. */
export function buildGrnSlipsHtml(slips: GrnSlipData[], opts: SlipOpts): string {
  const title = slips.length === 1 ? slips[0].grnId : `Goods receipts (${slips.length})`;
  return page(title, slips.map((g) => slipBody(g, opts)).join(''));
}
